/**
 * In-memory "สคริปต์แนะนำรายคน" for the mock mode (screens without an API). Tests pass their own scripts; without
 * them the demo scripts below are served — only on the app's own mock chats (MockChatApi.demo), so a test that brings
 * its own chat db sees no suggestion and the draft box works exactly as before.
 *   c101 Pim Chanok — new customer, asks about shipping to เชียงใหม่ (stage 2/6; ขั้นตอนการขาย: the place is current —
 *                      เชียงใหม่ needs the postcode)
 *   c102 คุณมายด์   — returning: buys the same pack every ~14 days, late now, one late delivery before (apology =
 *                      an alternative only), upsell + "สั่งชุดเดิมซ้ำ"; ขั้นตอนลูกค้าเก่า with an owner-added step
 *   c103 คุณเบียร์   — nothing to suggest (sent a slip): the draft box stays as it is today
 *   c107 web visitor — new, the website said the payment failed (problem card)
 * Values that would come from POS are bracketed placeholders like in the approved mockup — never invented prices.
 */
import { stepStatuses, withTick } from '../../lib/script'
import { ApiError } from '../client'
import { getApi } from '../index'
import { MockChatApi } from '../mock'
import type { MockDb } from '../mockData'
import type { ChatMessage } from '../types'
import type {
  ConversationScript,
  OrderRead,
  RepeatOrder,
  ScriptApi,
  ScriptPart,
  ScriptPending,
  ScriptStep,
  ScriptSuggestion,
  ScriptUndefinedTopic,
  ScriptUpdatedEvent,
} from './types'

// ---- mock socket: connectMock (../realtime.ts) forwards these
type Listener = (e: ScriptUpdatedEvent) => void
const listeners = new Set<Listener>()
export function onMockScript(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

const DAY = 86400_000
const clone = <T>(v: T): T => structuredClone(v)

const text = (t: string): ScriptPart => ({ t: 'text', text: t })
const fact = (t: string, source: string): ScriptPart => ({ t: 'fact', text: t, source })
const gap = (label: string): ScriptPart => ({ t: 'gap', label })

/** The whole message as one string, gaps as "[label]" (what the API sends in `text`). */
function joined(parts: ScriptPart[]): string {
  return parts.map((p) => (p.t === 'gap' ? `[${p.label}]` : p.text)).join('')
}

function suggestion(s: Omit<ScriptSuggestion, 'text'>): ScriptSuggestion {
  return { ...s, text: joined(s.parts) }
}

/** ขั้นตอนการขาย like the API: [key, label, question, done, value?] + สรุปยอด → ชำระ (the default steps of
 *  api/app/ai/script/steps.py; statuses by the same rule). */
type StepRow = [key: string, label: string, question: string, done: boolean, value?: string]
export function mockSteps(rows: StepRow[], summary = false, pay = false): ScriptStep[] {
  const steps: ScriptStep[] = [
    ...rows.map(([key, label, question, done, value]): ScriptStep => ({
      key,
      label,
      question,
      status: done ? 'done' : 'todo',
      kind: key.startsWith('custom:') ? 'custom' : 'auto',
      value: value ?? null,
      asked: false,
    })),
    { key: 'summary', label: 'สรุปยอด', question: null, status: summary ? 'done' : 'system', kind: 'system', value: null },
    { key: 'pay', label: 'ชำระ', question: null, status: pay ? 'done' : 'system', kind: 'system', value: null },
  ]
  return stepStatuses(steps, (s) => s.status === 'done')
}

export const STEP_Q = {
  area: 'สะดวกแจ้งที่อยู่จัดส่ง หรือแชร์โลเคชันให้แอดมินได้ไหมคะ แอดมินจะเช็กค่าส่งและวันที่ได้รับให้เลยค่ะ',
  meals: 'ปกติคุณลูกค้าทานวันละกี่มื้อคะ แอดมินจะช่วยแนะนำแพ็กที่เหมาะให้ค่ะ',
  newPack: 'สนใจรับแพ็กไหนดีคะ ถ้ายังไม่แน่ใจ แอดมินช่วยแนะนำให้ได้นะคะ',
  avoid: 'มีอาหารที่แพ้ หรือเมนูที่ไม่ทานไหมคะ แอดมินจะได้เลือกให้ถูกใจค่ะ',
  menu: 'คุณลูกค้าสะดวกเลือกเมนูเอง หรือให้แอดมินช่วยจัดให้ดีคะ',
  start: 'สะดวกเริ่มรับอาหารวันไหนดีคะ',
  address: 'รอบนี้จัดส่งที่อยู่เดิมเหมือนครั้งก่อนได้เลยไหมคะ',
  pack: 'รอบนี้รับแพ็กเดิมเหมือนครั้งก่อนไหมคะ หรืออยากลองเปลี่ยนขนาดแพ็กดีคะ',
  avoidAgain: 'รอบนี้มีเมนูที่ไม่ทาน หรืออยากเปลี่ยนเมนูไหมคะ',
} as const

/** The customer messages after the shop's last reply (like the API's `pending`). */
function pendingOf(db: MockDb, convId: string, topics: Record<number, [string, string]> = {}): ScriptPending[] {
  const items = (db.threads.get(convId) ?? []).filter((x): x is ChatMessage => x.kind === 'message')
  const lastOut = items.map((m) => m.direction).lastIndexOf('out')
  return items.slice(lastOut + 1).flatMap((m, i) => {
    if (m.direction !== 'in') return []
    const topic = topics[i] ?? null
    const t = m.text ?? (m.type === 'image' ? `[รูปภาพ${m.attachments[0]?.name ? `: ${m.attachments[0].name}` : ''}]` : `[${m.type}]`)
    return [{ message_id: m.id, text: t, at: m.at, topic: topic?.[0] ?? null, topic_label: topic?.[1] ?? null }]
  })
}

export function demoScripts(db: MockDb): Record<string, ConversationScript> {
  const iso = (msAgo: number) => new Date(db.now - msAgo).toISOString()
  const computed = iso(20_000)
  const base = { computed_at: computed, stale: false, enabled: true, undefined_topics: [] as ScriptUndefinedTopic[] }
  const out: Record<string, ConversationScript> = {}

  // ---- c101: new customer (FB), "ส่งต่างจังหวัดได้ไหมคะ อยู่เชียงใหม่" — stage 2/6, fee needs the postcode
  out.c101 = {
    ...base,
    conversation_id: 'c101',
    segment: 'new',
    // แนวลูกค้า as api/app/ai/script/style.py reads "สวัสดีค่ะ" + "ส่งต่างจังหวัดได้ไหมคะ อยู่เชียงใหม่"
    style: { key: 'polite_short', label: 'สุภาพ กระชับ', returning: false, motives: [], basis: 2 },
    stage: { n: 2, of: 6, key: 'fee_date', label: 'ค่าส่ง + วันได้ของ' },
    pending: pendingOf(db, 'c101', { 0: ['greeting', 'ทักทาย'], 1: ['ship_area', 'พื้นที่ส่ง'] }),
    known: [{ key: 'area', label: 'พื้นที่', value: 'เชียงใหม่', source: 'chat' }],
    missing: [
      { key: 'postcode', label: 'รหัสไปรษณีย์', hint: 'ต่างจังหวัดใช้รหัสไปรษณีย์คิดค่าส่ง DHL' },
      { key: 'meals_per_day', label: 'กินวันละกี่มื้อ', hint: null },
      { key: 'allergy', label: 'แพ้อาหาร', hint: null },
      { key: 'start_date', label: 'เริ่มรับวันไหน', hint: null },
    ],
    steps: mockSteps([
      ['meals', 'กินวันละกี่มื้อ', STEP_Q.meals, false],
      ['pack', 'เลือกแพ็ก', STEP_Q.newPack, false],
      ['area', 'ที่อยู่จัดส่ง', 'รบกวนคุณลูกค้าแจ้งรหัสไปรษณีย์ของที่อยู่จัดส่งให้แอดมินได้ไหมคะ แอดมินจะได้เช็กวันที่ได้รับให้ค่ะ', false, 'จ.เชียงใหม่'],
      ['avoid', 'แพ้อาหาร / ไม่ทาน', STEP_Q.avoid, false],
      ['menu', 'เลือกเมนู', STEP_Q.menu, false],
      ['start', 'เริ่มรับวันไหน', STEP_Q.start, false],
    ]),
    suggestions: [
      suggestion({
        id: 'c101-s1',
        kind: 'answer',
        title: 'ตอบ 2 ข้อความที่ค้าง · ขอรหัสไปรษณีย์เพื่อบอกค่าส่งเป็นตัวเลข',
        parts: [
          // paragraphs like the API (owner 17 ต.ค. 69 "ไม่ติดกันเป็นพืด"): greeting · fee · day · the question last
          text('สวัสดีค่ะคุณ Pim\n\nส่ง'),
          fact('เชียงใหม่', 'chat'),
          text('ได้ค่ะ จัดส่งแบบแช่เย็นทาง '),
          fact('DHL', 'shipping'),
          text(' ค่าส่ง '),
          gap('ค่าส่ง'),
          text('\n\nได้ของ'),
          fact('[วันส่งแรก]', 'pos'),
          text(' ค่ะ\n\nรบกวนขอรหัสไปรษณีย์หน่อยนะคะ จะเช็คค่าส่งให้ตรงเป๊ะค่ะ'),
        ],
        why: [
          { text: 'ลูกค้าถามเรื่องส่งต่างจังหวัด และบอกว่าอยู่เชียงใหม่', source: 'chat' },
          { text: 'ได้ตัวเลขค่าส่งจริงปิดได้ 75% · ไม่ได้ตัวเลข 13.5%', source: 'stats' },
          { text: 'ต่างจังหวัดต้องมีรหัสไปรษณีย์ก่อน ค่าส่งจึงเว้นไว้ให้เติม', source: 'shipping' },
        ],
      }),
      suggestion({
        id: 'c101-s2',
        kind: 'ask',
        title: 'ถามจำนวนมื้อและแพ้อาหาร',
        parts: [text('ทานวันละกี่มื้อคะ และมีอาหารที่แพ้ไหมคะ แอดมินจะได้แนะนำแพ็กให้เหมาะค่ะ')],
        why: [{ text: '70% ของลูกค้าใหม่เริ่มที่แพ็ก 7 วัน · ต้องรู้จำนวนมื้อก่อนเสนอแพ็ก', source: 'stats' }],
      }),
    ],
    returning: null,
    problems: [],
  }

  // ---- c102: returning (LINE) — same pack twice every ~14 days, 30 days later than usual, a late round before
  const c102 = db.conversations.get('c102')
  const stats = c102?.pos?.stats
  out.c102 = {
    ...base,
    conversation_id: 'c102',
    segment: 'returning',
    style: { key: 'polite_detailed', label: 'สุภาพ ละเอียด', returning: true, motives: [{ key: 'avoid', label: 'แพ้/งด' }], basis: 3 },
    stage: null,
    pending: pendingOf(db, 'c102', { 0: ['ship_fee', 'ค่าส่ง'], 1: ['allergy', 'แพ้อาหาร'] }),
    known: [
      { key: 'allergy', label: 'แพ้อาหาร', value: 'แพ้กุ้ง', source: 'pos' },
      { key: 'preference', label: 'ชอบ', value: 'เผ็ดน้อย', source: 'chat' },
      { key: 'area', label: 'พื้นที่', value: 'บางนา', source: 'pos' },
    ],
    missing: [],
    steps: mockSteps([
      ['address', 'ที่อยู่เดิม', STEP_Q.address, true, 'ที่อยู่เดิม'],
      ['pack', 'แพ็กเดิมหรือเปลี่ยน', STEP_Q.pack, false],
      ['avoid', 'เมนูที่ไม่ทาน', STEP_Q.avoidAgain, false],
      ['start', 'เริ่มรับวันไหน', STEP_Q.start, false],
      ['custom:1', 'แจ้งเมนูใหม่รอบนี้', 'รอบนี้มีเมนูใหม่ด้วยนะคะ สนใจลองไหมคะ', false],
    ]),
    suggestions: [
      suggestion({
        id: 'c102-s1',
        kind: 'answer',
        title: 'ตอบค่าส่งบางนา + เรื่องแพ้กุ้งจากข้อมูลรอบก่อน',
        parts: [
          text('สวัสดีค่ะคุณมายด์\n\nส่งแถว'),
          fact('บางนา', 'pos'),
          text(' ค่าส่ง '),
          fact('[฿ค่าส่ง]', 'pos'),
          text(' ค่ะ สั่งก่อน 22:00 ได้ของ'),
          fact('[วันส่งแรก]', 'pos'),
          text(' ค่ะ\n\nเรื่องแพ้กุ้ง แอดมินเลือกเมนูที่ไม่มีกุ้งให้เหมือนรอบก่อนได้เลยค่ะ ใส่'),
          fact('อกไก่ย่างเทริยากิ', 'pos'),
          text(' ที่ชอบไว้ด้วยไหมคะ'),
        ],
        why: [
          { text: 'ถามค่าส่งและเรื่องแพ้กุ้ง ยังไม่มีใครตอบ', source: 'chat' },
          { text: 'แพ้กุ้ง · เมนูที่สั่งซ้ำบ่อย อกไก่ย่างเทริยากิ', source: 'pos' },
          { text: 'ลูกค้าเก่าไม่ต้องถามที่อยู่ซ้ำ ใช้ที่อยู่บางนาเดิม', source: 'pos' },
        ],
      }),
      suggestion({
        id: 'c102-s2',
        kind: 'upsell',
        title: 'ชวนสั่งแพ็กใหญ่ขึ้น',
        parts: [
          text('รอบนี้ลองเป็น'),
          fact('[แพ็กถัดไป]', 'pos'),
          text('ไหมคะ มื้อละถูกลง '),
          fact('[฿ส่วนต่างต่อมื้อ]', 'pos'),
          text(' และไม่ต้องสั่งบ่อยค่ะ'),
        ],
        why: [{ text: '2 รอบล่าสุดสั่งแพ็กเดิมซ้ำ ทุก ~14 วัน', source: 'pos' }],
      }),
      suggestion({
        id: 'c102-s3',
        kind: 'apology',
        title: 'ขอโทษเรื่องรอบก่อนส่งช้า',
        parts: [
          text('ต้องขอโทษอีกครั้งเรื่องรอบก่อนที่ของถึงช้ากว่านัด '),
          fact('[40 นาที]', 'shipping'),
          text(' นะคะ\n\nรอบนี้แอดมินจะดูแลให้ตรงเวลาค่ะ'),
        ],
        why: [{ text: 'รอบก่อนถึงช้ากว่านัด · ไม่ใช่คำแนะนำแรก (เจ้าของเคาะ)', source: 'shipping' }],
      }),
    ],
    returning: {
      orders: stats?.orders_count ?? 3,
      total: stats?.total_spent ?? null,
      last_at: stats?.last_order_at ?? iso(44 * DAY),
      usual_gap_days: 14,
      overdue_days: 30,
      // pack names as the API writes them for the customer ("แพ็ก 7 วัน 14 มื้อ", owner 17 ต.ค. 69), not the POS name
      last_pack: { name: 'แพ็ก 7 วัน 14 มื้อ', meals: 14 },
      upsell: {
        reason: 'same_pack_repeated',
        detail: '2 รอบล่าสุดสั่งแพ็ก 7 วัน 14 มื้อ ทุก ~14 วัน',
        from: { name: 'แพ็ก 7 วัน 14 มื้อ', meals: 14, per_meal: null },
        to: { name: '[แพ็กถัดไป]', meals: null, per_meal: null },
        suggestion_id: 'c102-s2',
      },
      open_meals: 4,
      repeat_last_order: { available: true, summary: 'แพ็ก 7 วัน 14 มื้อ · 3 เมนู' },
    },
    problems: [{ kind: 'delivery_late', text: 'รอบก่อนถึงช้ากว่านัด [40 นาที] · ขอโทษลูกค้าแล้ว', at: iso(16 * DAY) }],
  }

  // ---- c103: nothing to suggest (a slip + a photo; the slip check already answered)
  out.c103 = {
    ...base,
    conversation_id: 'c103',
    segment: 'returning',
    style: { key: 'polite_short', label: 'สุภาพ กระชับ', returning: true, motives: [], basis: 0 },
    stage: null,
    pending: pendingOf(db, 'c103'),
    known: [],
    missing: [],
    steps: mockSteps(
      [
        ['address', 'ที่อยู่เดิม', STEP_Q.address, true, 'ที่อยู่เดิม'],
        ['pack', 'แพ็กเดิมหรือเปลี่ยน', STEP_Q.pack, true, 'แพ็กเดิม'],
        ['avoid', 'เมนูที่ไม่ทาน', STEP_Q.avoidAgain, true, 'ไม่มี'],
        ['start', 'เริ่มรับวันไหน', STEP_Q.start, true, 'พรุ่งนี้'],
      ],
      true,
    ),
    suggestions: [],
    returning: {
      orders: 1,
      total: db.conversations.get('c103')?.pos?.stats.total_spent ?? null,
      last_at: db.conversations.get('c103')?.pos?.stats.last_order_at ?? null,
      usual_gap_days: null,
      overdue_days: null,
      last_pack: null,
      upsell: null,
      open_meals: null,
      repeat_last_order: null,
    },
    problems: [],
  }

  // ---- c107: web visitor, the website said the payment failed
  out.c107 = {
    ...base,
    conversation_id: 'c107',
    segment: 'new',
    style: { key: 'polite_short', label: 'สุภาพ กระชับ', returning: false, motives: [], basis: 1 },
    stage: { n: 6, of: 6, key: 'pay', label: 'ชำระ + ทักตาม' },
    pending: pendingOf(db, 'c107', { 0: ['order_change', 'แก้คำสั่งซื้อ'] }),
    known: [{ key: 'channel', label: 'สั่งทาง', value: 'หน้าเว็บ', source: 'web' }],
    missing: [
      { key: 'order_ref', label: 'เลขคำสั่งซื้อ', hint: null },
      { key: 'new_address', label: 'ที่อยู่ใหม่', hint: null },
    ],
    steps: mockSteps(
      [
        ['meals', 'กินวันละกี่มื้อ', STEP_Q.meals, true, 'แพ็ก 7 วัน 21 มื้อ'],
        ['pack', 'เลือกแพ็ก', STEP_Q.newPack, true, 'แพ็ก 7 วัน 21 มื้อ'],
        ['area', 'ที่อยู่จัดส่ง', STEP_Q.area, true, 'เขตห้วยขวาง'],
        ['avoid', 'แพ้อาหาร / ไม่ทาน', STEP_Q.avoid, true, 'ไม่มี'],
        ['menu', 'เลือกเมนู', STEP_Q.menu, true, 'ตะกร้าบนเว็บ 7 รายการ'],
        ['start', 'เริ่มรับวันไหน', STEP_Q.start, true, 'พรุ่งนี้'],
      ],
      true,
    ),
    suggestions: [
      suggestion({
        id: 'c107-s1',
        kind: 'answer',
        title: 'ตอบเรื่องแก้ที่อยู่ + แจ้งว่าการชำระเงินยังไม่ผ่าน',
        parts: [
          text('แก้ที่อยู่ได้ค่ะ รบกวนแจ้งเลขคำสั่งซื้อพร้อมที่อยู่ใหม่ด้วยนะคะ\n\nระบบแจ้งว่าการชำระเงินยังไม่สำเร็จ '),
          text('แอดมินช่วยดูให้ได้ค่ะ ถ้าสะดวกชำระผ่านแชทนี้ได้เลยนะคะ'),
        ],
        why: [
          { text: 'หน้าเว็บแจ้งลูกค้าว่าชำระเงินไม่สำเร็จ', source: 'web' },
          { text: 'ได้ใบสรุปยอดแล้วชำระ 97%', source: 'stats' },
        ],
      }),
    ],
    returning: null,
    problems: [{ kind: 'payment_failed', text: 'หน้าเว็บแจ้งลูกค้าว่าชำระเงินไม่สำเร็จ', at: iso(2 * 60_000) }],
  }
  return out
}

/** c102's "สั่งชุดเดิมซ้ำ": the 7-day pack of the POS catalog + 3 menus (api/pos/mock.ts MOCK_CATALOG). */
export const DEMO_CARTS: Record<string, RepeatOrder> = {
  c102: {
    cart: {
      set_menu_id: 104,
      set_menu_name: 'Pro 7 day 21 มื้อ ลด 5%',
      items: [
        { menu_id: 101, qty: 7 },
        { menu_id: 237, qty: 7 },
        { menu_id: 240, qty: 7 },
      ],
      shipping: 'lalamove',
      label: 'สั่งชุดเดิมซ้ำ (แพ็ก 7 วัน)',
    },
  },
}

export interface MockScriptOptions {
  /** Tests: the scripts to serve (none = the demo scripts, on the app's own mock chats only). */
  scripts?: Record<string, ConversationScript>
  /** "สั่งชุดเดิมซ้ำ" carts (default: the demo cart of c102). */
  carts?: Record<string, RepeatOrder>
  latencyMs?: number
  /** "Now" for a refresh's computed_at (tests pass their clock). */
  now?: () => number
  /** "เติมจากแชททั้งหมด" answers per chat (none = the demo script's `order_prefill`, on the app's own mock chats). */
  reads?: Record<string, OrderRead>
}

type MockCall = 'get' | 'refresh' | 'repeatOrder' | 'stepDone' | 'orderRead'

export class MockScriptApi implements ScriptApi {
  /** Every call, in order (tests). */
  readonly calls: { method: MockCall; id: string; key?: string; done?: boolean; auto?: boolean }[] = []
  /** Tests: the next call fails like the API would. */
  failNext: { status: number; detail: string } | null = null
  private scripts: Map<string, ConversationScript> | null
  private carts: Map<string, RepeatOrder>
  private reads: Map<string, OrderRead>
  private demoDb: MockDb | null = null
  private demo = new Map<string, ConversationScript>()
  private readonly latency: number
  private readonly now: () => number

  constructor(opts: MockScriptOptions = {}) {
    this.scripts = opts.scripts ? new Map(Object.entries(clone(opts.scripts))) : null
    this.carts = new Map(Object.entries(clone(opts.carts ?? DEMO_CARTS)))
    this.reads = new Map(Object.entries(clone(opts.reads ?? {})))
    this.latency = opts.latencyMs ?? (opts.scripts ? 0 : 150)
    this.now = opts.now ?? (() => Date.now())
  }

  /** The demo scripts follow the app's own mock db (rebuilt if that db changes). */
  private store(): Map<string, ConversationScript> {
    if (this.scripts) return this.scripts
    const chat = getApi()
    if (!(chat instanceof MockChatApi) || !chat.demo) return new Map()
    if (this.demoDb !== chat.db) {
      this.demoDb = chat.db
      this.demo = new Map(Object.entries(demoScripts(chat.db)))
    }
    return this.demo
  }

  private async step(method: MockCall, id: string, extra = {}): Promise<void> {
    this.calls.push({ method, id, ...extra })
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency))
    const f = this.failNext
    if (f) {
      this.failNext = null
      throw new ApiError(f.status, f.detail)
    }
  }

  async get(convId: string): Promise<ConversationScript | null> {
    await this.step('get', convId)
    const s = this.store().get(convId)
    return s ? clone(s) : null
  }

  async refresh(convId: string): Promise<ConversationScript | null> {
    await this.step('refresh', convId)
    const s = this.store().get(convId)
    if (!s) return null
    s.stale = false
    s.computed_at = new Date(this.now()).toISOString()
    return clone(s)
  }

  async repeatOrder(convId: string): Promise<RepeatOrder | null> {
    await this.step('repeatOrder', convId)
    const c = this.carts.get(convId)
    return c ? clone(c) : null
  }

  /** Like the API: only a step the owner added (custom:<n>) can be ticked by staff. */
  async stepDone(convId: string, key: string, done: boolean): Promise<ConversationScript> {
    await this.step('stepDone', convId, { key, done })
    const s = this.store().get(convId)
    if (!s) throw new ApiError(404, 'ไม่พบห้องแชทนี้')
    const st = s.steps.find((x) => x.key === key)
    if (!st) throw new ApiError(404, 'ไม่พบขั้นนี้ในขั้นตอนการขาย (เจ้าของอาจลบไปแล้ว)')
    if (!key.startsWith('custom:')) throw new ApiError(422, 'ขั้นนี้ระบบติ๊กเองจากแชท ติ๊กเองไม่ได้')
    s.steps = withTick(s.steps, key, done)
    return clone(s)
  }

  /** "เติมจากแชททั้งหมด": the test's answer, else (demo) what the demo script's summary handoff knows. */
  async orderRead(convId: string, opts: { auto?: boolean } = {}): Promise<OrderRead> {
    await this.step('orderRead', convId, { auto: !!opts.auto })
    const r = this.reads.get(convId)
    if (r) return clone(r)
    const pre = this.scripts ? null : this.store().get(convId)?.order_prefill
    if (!pre) throw new ApiError(422, 'แชทนี้ยังไม่มีข้อมูลให้อ่าน')
    return {
      conversation_id: convId,
      for_message_id: '0',
      cart: clone(pre),
      customer: { name: null, phone: null, address: pre.address ?? null },
      missing: ['เมนู', 'ชื่อผู้รับ', 'เบอร์โทร'],
      notes: [],
      usage: { model: 'mock', tokens_in: 0, tokens_out: 0, ms: 0, cost_usd: 0, cost_thb: null, cached: false },
    }
  }

  // ---- tests / demo: change a script, then tell the screens like the API does (WS script.updated)
  set(convId: string, script: ConversationScript | null): void {
    const m = this.store()
    if (script) m.set(convId, clone(script))
    else m.delete(convId)
  }

  push(convId: string): void {
    // like the API after a computation: the customer's style for the inbox row rides along (api/app/inbox.py)
    const style = this.store().get(convId)?.style?.key ?? null
    const e: ScriptUpdatedEvent = { type: 'script.updated', conversation_id: convId, style_key: style }
    for (const fn of listeners) fn({ ...e })
  }
}
